import React, { useState, useEffect } from "react";
import Chart from "react-apexcharts";
import "bootstrap/dist/css/bootstrap.min.css";

const CloudMapComplianceDetail = ({ isSidebarOpen }) => {
  const [connectedAccounts, setConnectedAccounts] = useState([]);

  useEffect(() => {
    // Retrieve connected accounts from localStorage
    const accounts = localStorage.getItem("connectedAccounts");
    setConnectedAccounts(accounts ? JSON.parse(accounts) : []);
  }, []);

  const handleConnectAccount = () => {
    // Redirect user to the connected accounts page
    window.location.href = "/dashboard/connected-accounts";
  };

  const metrics = [
    { label: "Sessions", value: 2845, change: "+12.4%", color: "text-success" },
    { label: "Orders", value: "$1,286", change: "-13.4%", color: "text-danger" },
    { label: "Generated Leads", value: 4350, change: "+22.3%", color: "text-success" },
    { label: "Expenses", value: 4234, change: "-8.2%", color: "text-danger" },
  ];

  const radarChartOptions = {
    chart: { type: "radar" },
    stroke: { width: 2 },
    labels: ["SOC2", "GDPR", "HIPAA", "ISO 27001", "PCI-DSS"],
    colors: ["#6D5BFF"],
  };

  const radarChartData = [{ name: "Compliance Scores", data: [85, 90, 70, 95, 80] }];

  const lineChartOptions = {
    chart: { type: "line", sparkline: { enabled: false } },
    stroke: { curve: "smooth", width: 2 },
    colors: ["#6D5BFF"],
    xaxis: { categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul"] },
  };

  const lineChartSeries = [{ name: "Resolved Issues", data: [20, 30, 40, 50, 60, 70, 80] }];

  const barChartOptions = {
    chart: { type: "bar" },
    plotOptions: { bar: { borderRadius: 4 } },
    colors: ["#6D5BFF"],
    xaxis: { categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul"] },
  };

  const barChartSeries = [{ name: "Expenses", data: [10, 20, 15, 30, 25, 35, 40] }];

  const pieChartOptions = {
    chart: { type: "pie" },
    labels: ["Critical", "High", "Medium", "Low"],
    colors: ["#f44336", "#ff9800", "#4caf50", "#2196f3"],
  };

  const pieChartSeries = [20, 30, 25, 25];

  const donutChartOptions = {
    chart: { type: "donut" },
    labels: ["Engineering", "Marketing", "Sales", "Support"],
    colors: ["#f44336", "#e83e8c", "#ff9800", "#4caf50"],
  };

  const donutChartSeries = [30, 25, 25, 20];

  const stackedBarChartOptions = {
    chart: { type: "bar", stacked: true },
    xaxis: { categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul"] },
    colors: ["#6D5BFF", "#2196f3", "#f44336"],
  };

  const stackedBarChartSeries = [
    { name: "Critical", data: [10, 15, 20, 25, 30, 35, 40] },
    { name: "High", data: [5, 10, 15, 20, 25, 30, 35] },
    { name: "Low", data: [2, 5, 8, 10, 15, 20, 25] },
  ];

  if (!connectedAccounts || connectedAccounts.length === 0) {
    return (
      <div
        className="d-flex justify-content-center align-items-center vh-100"
        style={{
          backgroundImage: 'url("/images/blurred_image.png")', // Replace with your background image URL
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div
          className="text-center text-dark p-4"
          style={{ backgroundColor: "rgba(170, 172, 242, 0.33)", borderRadius: "8px" }}
        >
          <h3>No Connected Accounts Found</h3>
          <p>
            To access this dashboard, please connect at least one account.
            Click the button below to get started.
          </p>
          <button className="btn text-white mt-3" style={{backgroundColor : '#342ead'}}
            onClick={handleConnectAccount}
          >
            Connect Account
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="cloudmap-page container-fluid"
      style={{ transition: "margin-left 0.3s ease", marginLeft: isSidebarOpen ? "0px" : "0" }}
    >
      <div className="container">
        <h2 className="text-muted mb-4" style={{ color: "#111" }}>
          CloudMap - Compliance Detail
        </h2>

        {/* Metrics Section */}
        <div className="row mb-4">
          {metrics.map((metric, index) => (
            <div className="col-md-3 col-6 mb-3" key={index}>
              <div className="rounded shadow p-3">
                <h6 className="mb-1 text-muted fw-bold text-uppercase">{metric.label}</h6>
                <h3 className="fw-bold mb-3" style={{ fontSize: "2rem", color: "#666cff" }}>
                  {metric.value}
                </h3>
                <span className={`fw-bold ${metric.color}`}>{metric.change}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Charts Section */}
        <div className="row">
          <div className="col-md-6 mb-4">
            <div className="rounded shadow p-3">
              <h6 className="text-uppercase text-muted fw-bold mb-3">Compliance Scores</h6>
              <Chart options={radarChartOptions} series={radarChartData} type="radar" height={250} />
            </div>
          </div>

          <div className="col-md-6 mb-4">
            <div className="rounded shadow p-3">
              <h6 className="text-uppercase text-muted fw-bold mb-3">Resolved Issues Over Time</h6>
              <Chart options={lineChartOptions} series={lineChartSeries} type="line" height={250} />
            </div>
          </div>

          <div className="col-md-6 mb-4">
            <div className="rounded shadow p-3">
              <h6 className="text-uppercase text-muted fw-bold mb-3">Expenses Over Time</h6>
              <Chart options={barChartOptions} series={barChartSeries} type="bar" height={250} />
            </div>
          </div>

          <div className="col-md-6 mb-4">
            <div className="rounded shadow p-3">
              <h6 className="text-uppercase text-muted fw-bold mb-3">Issue Severity Distribution</h6>
              <Chart options={pieChartOptions} series={pieChartSeries} type="pie" height={260} />
            </div>
          </div>

          <div className="col-md-6 mb-4">
            <div className="rounded shadow p-3">
              <h6 className="text-uppercase text-muted fw-bold mb-3">Resource Allocation</h6>
              <Chart options={donutChartOptions} series={donutChartSeries} type="donut" height={250} />
            </div>
          </div>

          <div className="col-md-6 mb-4">
            <div className="rounded shadow p-3">
              <h6 className="text-uppercase text-muted fw-bold mb-3">Monthly Compliance Activities</h6>
              <Chart
                options={stackedBarChartOptions}
                series={stackedBarChartSeries}
                type="bar"
                height={250}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CloudMapComplianceDetail;
